/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import {
  Asset,
  AssetTranscriptWord,
  BlockTranscriptSegment,
} from "types/Asset";
import { BlockToken } from "types/Block";
import { ClipWithPlaylistTimes } from "types/Clip";
import { v4 as uuidv4 } from "uuid";
import { BlockTypes } from "constants/BlockTypes";

function buildTokenFromWord(
  idPrefix: string,
  word: AssetTranscriptWord,
  transcriptSegmentIndex: number | null,
  segmentWordIndex: number | null,
  clipStartTime: number,
  offset: number,
  assetId: string
): BlockToken {
  return {
    id: `${idPrefix}-${word.id}`,
    assetId,
    text: word.text,
    transcriptSegmentIndex,
    segmentWordIndex,
    playlistStartTime: clipStartTime + word.startTime - offset,
    playlistEndTime: clipStartTime + word.endTime - offset,
  };
}

function buildSpacerToken(
  idPrefix: string,
  tokenStartTime: number,
  tokenEndTime: number,
  assetId: string,
  spaceId?: string
): BlockToken {
  return {
    id: `${idPrefix}-${spaceId ?? uuidv4()}`,
    assetId,
    text: null,
    transcriptSegmentIndex: null,
    segmentWordIndex: null,
    playlistStartTime: tokenStartTime,
    playlistEndTime: tokenEndTime,
  };
}

// TODO: Create selector/cache?
export function generateClipTranscript(
  clip: ClipWithPlaylistTimes,
  asset: Asset | null
): BlockTranscriptSegment[] {
  if (!asset?.transcript?.length) {
    return [];
  }

  const idPrefix =
    clip.type === BlockTypes.TRANSCRIPTION ? clip.tokenIdPrefix : clip.id;

  const assetSegments = asset.transcript;
  const assetStartTime = clip.offset;
  const assetEndTime = assetStartTime + clip.duration;

  const blockSegments: BlockTranscriptSegment[] = [];

  assetSegments.forEach((assetSegment, assetSegmentIndex) => {
    const tokensInClip: BlockToken[] = [];

    assetSegment.words.forEach((word, wordIndex) => {
      if (word.startTime >= assetStartTime && word.endTime <= assetEndTime) {
        tokensInClip.push(
          buildTokenFromWord(
            idPrefix,
            word,
            assetSegmentIndex,
            wordIndex,
            clip.startTime,
            assetStartTime,
            asset.id
          )
        );
      }
    });

    if (tokensInClip.length > 0) {
      const { words, ...blockSegmentSansTokens } = assetSegment;
      blockSegments.push({
        ...blockSegmentSansTokens,
        tokens: tokensInClip,
        segmentIndex: assetSegmentIndex,
      });
    }
  });

  // Add spacer tokens
  return blockSegments.map((blockSegment, blockSegmentIndex) => {
    const clipTokens: BlockToken[] = [];

    blockSegment.tokens.forEach((token, tokenIndex) => {
      if (blockSegmentIndex === 0 && tokenIndex === 0) {
        // Space before first word
        clipTokens.push(
          buildSpacerToken(
            idPrefix,
            clip.startTime,
            token.playlistStartTime,
            asset.id
          )
        );
      }

      // The word
      clipTokens.push(token);

      const nextToken =
        blockSegment.tokens[tokenIndex + 1] ??
        blockSegments[blockSegmentIndex + 1]?.tokens[0];
      if (nextToken) {
        // Space between words
        const spaceId = `${token.id}-${nextToken.id}`;
        clipTokens.push(
          buildSpacerToken(
            idPrefix,
            token.playlistEndTime,
            nextToken.playlistStartTime,
            asset.id,
            spaceId
          )
        );
      } else {
        // Space after last word
        clipTokens.push(
          buildSpacerToken(
            idPrefix,
            token.playlistEndTime,
            clip.endTime,
            asset.id
          )
        );
      }
    });

    blockSegment.tokens = clipTokens;
    return blockSegment;
  });
}
